import React from "react";
import api from "@/lib/api";
import {
  X, Trash, PaperPlaneRight, Robot, User as UserIcon, Paperclip,
  CheckCircle, Gear, ClipboardText, MagnifyingGlass, ArrowSquareOut,
  FilePdf, WarningCircle, Sparkle,
  SquaresFour, ChatCircleText, Clock, CaretRight, ArrowLeft,
  Receipt, ArrowClockwise,
  DownloadSimple, Prohibit,
  LockSimple, LockSimpleOpen, HandPalm, Copy, Hash, ArrowCounterClockwise, Envelope, XCircle,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import { useAuth } from "@/context/AuthContext";
import WizardFields from "@/components/erp/WizardFields";

// Clone base: frontend/src/components/erp/PurchaseReqAgentModal.jsx (post-restyle,
// ConfirmReviewPopup + quick-action-button + read-only-preparer-line version).
// Structure/logic mirrors that file line-for-line where PO has no divergent
// semantics; deltas (Buyer auto-resolution, Schedule nesting level, PO's own
// lifecycle-action set, and dropping PR's batch/Excel + two-step charge-account
// flows for this first cut) are called out inline below.
const AGENT_KEY = "purchase-order-create";
const BRAND = "#0020C2";
const BASE = `/erp-agents/${AGENT_KEY}`;

const THINKING = [
  "Validating references against Oracle…",
  "Resolving procurement business unit…",
  "Checking the supplier and site…",
  "Running pre-flight validation…",
  "Assembling the purchase order draft…",
];

// PO lines have no PR-style `quantity_based` fixed-price flag (models.py's
// POLine always carries Price × Quantity) — simpler than PR's lineAmount.
const lineAmount = (ln) => Number(ln?.price || 0) * Number(ln?.quantity || 0);
function draftTotal(draft) {
  const lines = draft?.lines || [];
  return { sum: lines.reduce((a, ln) => a + lineAmount(ln), 0), ccy: lines[0]?.currency_code || draft?.currency_code || "" };
}

// Fetch a protected file through axios (so the Bearer token is sent) and hand it to
// the browser. `inline` opens it in a new tab (PDF preview); otherwise it downloads.
// Used by BOTH the chat PDF card and the dashboard so viewing a PDF works everywhere.
async function downloadViaApi(path, filename, { inline = false } = {}) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  if (inline) {
    window.open(url, "_blank");
  } else {
    const a = document.createElement("a");
    a.href = url; a.download = filename || "download";
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* ── parsed-document verification card (shown after a PDF/Word upload). PR's
   Excel batch-upload path is NOT ported here — PO has no bulk-create-from-
   spreadsheet flow yet (see app/api/po_agent.py's module docstring); this is
   PDF/Docx supporting-document attachment only. ──────────────────────────── */
function DetectChips({ label, values }) {
  if (!values || !values.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mb-1">
      <span className="text-[10px] uppercase tracking-wide text-gray-400 mr-1">{label}</span>
      {values.map((v, i) => (
        <span key={i} className="text-[11px] font-mono bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-gray-700">{v}</span>
      ))}
    </div>
  );
}
function ParsedDocCard({ parsed, fileName, busy, onCreateNew, onAttachExisting }) {
  const d = parsed?.detected || {};
  const nothing = !d.amounts?.length && !d.dates?.length && !d.emails?.length && !d.doc_numbers?.length;
  return (
    <div className="border-2 rounded-xl bg-white shadow-md p-3.5" style={{ borderColor: `${BRAND}40` }}>
      <div className="flex items-center gap-2 mb-2">
        <FilePdf size={16} weight="fill" className="text-red-500" />
        <span className="text-[13px] font-semibold text-gray-900">We read "{parsed?.file_name || fileName}" — please check the details</span>
      </div>
      <div className="mb-2">
        <DetectChips label="Amounts" values={d.amounts} />
        <DetectChips label="Dates" values={d.dates} />
        <DetectChips label="Doc no." values={d.doc_numbers} />
        <DetectChips label="Emails" values={d.emails} />
        {nothing && <div className="text-[11px] text-gray-400">No specific amounts/dates detected — check the extracted text below.</div>}
      </div>
      {parsed?.preview && (
        <div className="max-h-32 overflow-auto border border-gray-100 rounded-lg p-2 bg-gray-50 text-[11px] text-gray-600 whitespace-pre-wrap mb-3">
          {parsed.preview}{parsed.char_count > parsed.preview.length ? "…" : ""}
        </div>
      )}
      <p className="text-[11px] text-gray-500 mb-2">If these look right, choose what to do with it. You can correct anything in chat afterwards.</p>
      <div className="flex gap-2">
        <button disabled={busy} onClick={onCreateNew} className="flex-1 text-[12px] font-medium text-white py-2 rounded-lg disabled:opacity-50" style={{ background: BRAND }}>Create a PO from this</button>
        <button disabled={busy} onClick={onAttachExisting} className="flex-1 text-[12px] font-medium text-gray-700 py-2 rounded-lg border border-gray-300 disabled:opacity-50">Attach to an existing PO</button>
      </div>
    </div>
  );
}

// Minimal chip — the parsed content is kept server-side and NOT dumped here.
function FileChip({ name, url }) {
  return (
    <div className="inline-flex items-center gap-2 border border-gray-200 rounded-lg bg-white px-2.5 py-1.5 text-[12px] shadow-sm">
      <FilePdf size={15} weight="fill" className="text-red-500" />
      <span className="text-gray-700 truncate max-w-[220px]">{name}</span>
      {url && <button onClick={() => window.open(url, "_blank")} title="Open document" className="text-gray-400 hover:text-gray-700"><ArrowSquareOut size={13} /></button>}
    </div>
  );
}

/* ── embedded PDF viewer — fetches the viewPDF blob WITH auth and shows it inline
   in an <iframe>. Used identically by the chat card and the dashboard so "View
   PDF" embeds the document in-place rather than opening a blank tab. ───────── */
function PdfViewer({ path, filename, height = 520 }) {
  const [url, setUrl] = React.useState(null);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancel = false; let objUrl = null;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(path, { responseType: "blob" });
        if (cancel) return;
        objUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        setUrl(objUrl);
      } catch (e) {
        if (!cancel) setErr(e?.response?.status === 502
          ? "Oracle couldn't generate the PDF for this purchase order."
          : (e?.response?.status === 404 ? "Purchase order not found." : "Couldn't load the PDF."));
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; if (objUrl) setTimeout(() => URL.revokeObjectURL(objUrl), 2000); };
  }, [path]);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-gray-100 bg-white">
        <span className="text-[11px] text-gray-500 flex items-center gap-1.5"><FilePdf size={13} className="text-red-500" /> {filename || "Purchase Order PDF"}</span>
        {url && <a href={url} download={filename || "purchase_order.pdf"} className="text-[11px] text-[#0020C2] hover:underline flex items-center gap-1"><DownloadSimple size={12} /> Download</a>}
      </div>
      {loading ? <div className="flex items-center justify-center text-[12px] text-gray-400" style={{ height }}>Generating PDF from Oracle…</div>
        : err ? <div className="flex items-center justify-center text-[12px] text-red-600 px-4 text-center" style={{ height }}>{err}</div>
        : <iframe title={filename || "Purchase Order PDF"} src={url} className="w-full bg-white" style={{ height, border: 0 }} />}
    </div>
  );
}

/* ── chat PDF card — embeds the PDF inline in the conversation ──────────────── */
function PdfCard({ info }) {
  const [open, setOpen] = React.useState(true);   // embed immediately when offered
  const id = info?.po_header_id;
  const num = info?.order_number || id;
  return (
    <div className="border rounded-xl bg-white shadow-sm p-3 w-full" style={{ borderColor: `${BRAND}30` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[12px] text-gray-800"><FilePdf size={15} weight="fill" className="text-red-500" /> Purchase Order {num} PDF</div>
        <button onClick={() => setOpen((o) => !o)} className="text-[11px] text-[#0020C2] hover:underline">{open ? "Hide" : "View"}</button>
      </div>
      {open && <PdfViewer path={`${BASE}/pdf/${id}`} filename={`purchase_order_${num}.pdf`} height={460} />}
    </div>
  );
}

/* ── chat bubble ─────────────────────────────────────────────────────────── */
function Bubble({ msg, phraseIdx }) {
  const isUser = msg.role === "user";
  const assistantCls = msg.tone === "error" ? "bg-red-50 border border-red-300 text-red-800"
    : msg.tone === "success" ? "bg-emerald-50 border border-emerald-300 text-emerald-800"
    : "bg-white border border-gray-200 text-gray-900";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white"
        style={{ background: isUser ? BRAND : msg.tone === "error" ? "#DC2626" : msg.tone === "success" ? "#059669" : "#374151" }}>
        {isUser ? <UserIcon size={11} weight="bold" /> : msg.tone === "error" ? <WarningCircle size={11} weight="fill" /> : msg.tone === "success" ? <CheckCircle size={11} weight="fill" /> : <Robot size={11} weight="bold" />}
      </div>
      <div className={`max-w-[85%] px-3 py-2 text-[13px] leading-relaxed rounded-2xl ${isUser ? "text-white rounded-tr-sm" : `${assistantCls} shadow-sm rounded-tl-sm`}`}
        style={isUser ? { background: BRAND } : undefined}>
        {msg.loading ? (
          <div className="flex items-center gap-2 py-0.5">
            <div className="flex items-center gap-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full animate-bounce bg-gray-400" style={{ animationDelay: `${d}ms` }} />)}</div>
            <span className="text-[11px] text-gray-400">{THINKING[phraseIdx % THINKING.length]}</span>
          </div>
        ) : <p className="whitespace-pre-wrap break-words">{msg.text}</p>}
      </div>
    </div>
  );
}

/* ── inline field card (next_field_ui) ───────────────────────────────────── */
// `extraParams` (e.g. {supplier_id: 123} or {bu_id: 456}) scopes a dependent
// dropdown to valid values only — mirrors the platform's supplier_site-by-supplier
// pattern already proven for invoice-create, applied here for PO's own two-hop
// chain: supplier_site depends on supplier (a literal Oracle child resource per
// lov.py's resolve_supplier_site — suppliersLOV/{SupplierId}/child/sitesLOV).
function LookupSelect({ lookup, onPick, extraParams }) {
  const [q, setQ] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [note, setNote] = React.useState("");
  const extraKey = extraParams ? JSON.stringify(extraParams) : "";
  React.useEffect(() => {
    let cancel = false;
    const t = setTimeout(async () => {
      setLoading(true); setNote("");
      try {
        const { data } = await api.get(`/erp-agents/lookups/${lookup}`, { params: { q, limit: 20, ...(extraParams || {}) } });
        if (cancel) return;
        setRows(data.rows || []);
        if (data.status && data.status !== "ok") setNote(
          data.status === "unconfigured" ? "Connect your Oracle profile to load options."
          : data.status === "auth_error" ? "Oracle sign-in failed — check your credentials."
          : data.message || "Oracle couldn't return options right now.");
      } catch { if (!cancel) setNote("Couldn't load options."); }
      finally { if (!cancel) setLoading(false); }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, lookup, extraKey]);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-gray-100">
        <MagnifyingGlass size={13} className="text-gray-400" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full text-[13px] outline-none" />
      </div>
      {note && <div className="px-2.5 py-1.5 text-[11px] text-amber-700 bg-amber-50">{note}</div>}
      <div className="max-h-44 overflow-y-auto">
        {loading && <div className="px-2.5 py-2 text-[11px] text-gray-400">Searching Oracle…</div>}
        {!loading && rows.length === 0 && !note && <div className="px-2.5 py-2 text-[11px] text-gray-400">No matches.</div>}
        {rows.map((r) => (
          <button key={String(r.value)} onClick={() => onPick(r)} className="w-full text-left px-2.5 py-1.5 text-[13px] hover:bg-blue-50 border-b border-gray-50 last:border-0">
            <div className="text-gray-900">{r.label}</div>
            {r.sub && <div className="text-[10px] font-mono text-gray-400">{r.sub}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Lightweight per-field suggestion memory (this browser session only) — a lot
// of these text fields (description, note-to-supplier…) have no Oracle-backed
// lookup, so recently-typed values are the best available autocomplete source
// without inventing data.
const RECENT_KEY_PREFIX = "po_field_recent:";
function recentValues(field) {
  try { return JSON.parse(sessionStorage.getItem(RECENT_KEY_PREFIX + field) || "[]"); } catch { return []; }
}
function rememberValue(field, val) {
  if (!val) return;
  try {
    const cur = recentValues(field).filter((v) => v !== val);
    cur.unshift(val);
    sessionStorage.setItem(RECENT_KEY_PREFIX + field, JSON.stringify(cur.slice(0, 8)));
  } catch { /* sessionStorage unavailable — no autocomplete history, not fatal */ }
}

// Free-text field input with autocomplete: item_number gets live suggestions
// from the Oracle item catalog (same lookup the "item" dropdown uses); every
// other text field falls back to this session's own recently-typed values.
function TextFieldWithSuggestions({ fieldUi, val, setVal, onSubmit }) {
  const [open, setOpen] = React.useState(false);
  const [itemHits, setItemHits] = React.useState([]);
  const isItemNumber = fieldUi.field === "item_number";
  const recents = React.useMemo(() => recentValues(fieldUi.field), [fieldUi.field]);

  React.useEffect(() => {
    if (!isItemNumber || !val.trim()) { setItemHits([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/erp-agents/lookups/items", { params: { q: val.trim(), limit: 8 } });
        if (!cancel) setItemHits(data.rows || []);
      } catch { if (!cancel) setItemHits([]); }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [val, isItemNumber]);

  const suggestions = isItemNumber
    ? itemHits.map((r) => ({ value: r.label, hint: r.sub }))
    : recents.filter((v) => !val || v.toLowerCase().includes(val.toLowerCase())).map((v) => ({ value: v }));

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <input type={fieldUi.uiType === "number" ? "number" : fieldUi.uiType === "date" ? "date" : "text"}
          value={val} onChange={(e) => setVal(e.target.value)} placeholder={fieldUi.placeholder || ""}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
          className="flex-1 text-[13px] border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#0020C2]" />
        <button onClick={onSubmit} className="text-[12px] text-white px-3 py-1.5 rounded-lg" style={{ background: BRAND }}>Send</button>
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-md max-h-40 overflow-y-auto">
          {suggestions.slice(0, 8).map((s, i) => (
            <button key={i} type="button" onMouseDown={() => setVal(s.value)}
              className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-blue-50 border-b border-gray-50 last:border-0">
              {s.value}{s.hint ? <span className="text-[10px] text-gray-400 ml-1">{s.hint}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// `lookupContext` carries the most recently picked procurement_bu/supplier ids so a
// dependent field (supplier_site ← supplier) can scope its dropdown to valid values
// only. `onRawPick` lets the parent capture the raw {value,label} row (not just the
// composed chat string) to update that context.
function FieldCard({ fieldUi, disabled, onSubmit, onSkip, onRawPick, lookupContext }) {
  const [val, setVal] = React.useState("");
  const isLookup = fieldUi.uiType === "searchable_dropdown" && fieldUi.lookup;
  const submitPlain = () => {
    if (val === "") return;
    rememberValue(fieldUi.field, val);
    onSubmit(`${fieldUi.label}: ${val}`);
  };
  const extraParams =
    fieldUi.field === "supplier_site" ? (lookupContext?.supplierId ? { supplier_id: lookupContext.supplierId } : undefined)
    : fieldUi.field === "supplier" ? (lookupContext?.buId ? { bu_id: lookupContext.buId } : undefined)
    : undefined;
  const missingParent = fieldUi.field === "supplier_site" && !lookupContext?.supplierId;
  return (
    <div className={`border rounded-xl bg-white shadow-sm p-3 ${disabled ? "opacity-60" : ""}`} style={{ borderColor: `${BRAND}30` }}>
      <div className="text-[11px] font-semibold text-gray-700 mb-2">{fieldUi.label}{fieldUi.required && <span className="text-red-500 ml-0.5">*</span>}</div>
      {disabled ? <div className="text-[11px] text-gray-400 italic">Answered.</div>
        : missingParent ? <div className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2.5 py-2">Pick a supplier first — sites are scoped to it.</div>
        : isLookup ? <LookupSelect lookup={fieldUi.lookup} extraParams={extraParams}
            onPick={(r) => { onRawPick?.(fieldUi.field, r); if (fieldUi.field !== "buyer") onSubmit(`${fieldUi.label}: ${r.label} [id=${r.value}]`); }} />
        : fieldUi.uiType === "dropdown" ? (
          <div className="flex flex-col gap-1">{(fieldUi.options || []).map((o) => <button key={o.value} onClick={() => onSubmit(`${fieldUi.label}: ${o.value}`)} className="text-left text-[13px] px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-blue-50">{o.label}</button>)}</div>
        ) : (
          <TextFieldWithSuggestions fieldUi={fieldUi} val={val} setVal={setVal} onSubmit={submitPlain} />
        )}
      {!disabled && <button onClick={onSkip} className="mt-2 text-[11px] text-gray-400 hover:text-gray-600">Skip / answer in chat</button>}
    </div>
  );
}

// Field *derivation* for PO's ConfirmReviewPopup — maps the staged draft object
// (PurchaseOrderDraftIn.model_dump() shape, per erp_agents/purchase_order_create/
// tools/agent_tools.py) straight onto the {label, value} shape the shared popup
// renders. This stays local to this file: ConfirmReviewPopup itself has no
// knowledge of `draft`.
function poDraftToFields(draft) {
  return [
    { label: "Procurement BU", value: draft?.procurement_bu_name || draft?.procurement_bu_id || "—" },
    // Buyer is PO's Preparer-equivalent — always server-resolved from the logged-in
    // user's email before the chat starts (runner.py's _ensure_buyer_resolved), so
    // the draft only ever carries a numeric buyer_id; buyer_name/buyer_email are
    // checked first in case a future backend enriches the draft with a display name.
    { label: "Buyer", value: draft?.buyer_name || draft?.buyer_email || draft?.buyer_id || "—" },
    { label: "Supplier", value: draft?.supplier_name || draft?.supplier_id || "—" },
    { label: "Supplier site", value: draft?.supplier_site_name || draft?.supplier_site_id || "—" },
    { label: "Requester", value: draft?.requester_name || draft?.requester_id || "—" },
    { label: "Description", value: draft?.description || "—" },
  ];
}

// Lines + schedules + total breakdown, rendered in ConfirmReviewPopup's `children`
// slot (below the field grid, above Confirm/Cancel). Unlike PR's flat line→
// distribution shape, a PO line nests `schedules` (each with its own need-by/
// promised date and ship-to/deliver-to) — one hierarchy level deeper, per
// models.py's POLine/POSchedule.
function PoLinesBreakdown({ draft }) {
  const { sum, ccy } = draftTotal(draft);
  return (
    <div>
      <div className="border border-gray-100 rounded-lg overflow-hidden mb-2.5">
        {(draft?.lines || []).map((ln, i) => (
          <div key={i} className="px-2.5 py-1.5 text-[11px] border-b border-gray-50 last:border-0">
            <div className="font-medium text-gray-900">{ln.line_number}. {ln.item_description || (ln.item_id ? `Item ${ln.item_id}` : "Line")}</div>
            <div className="text-gray-500">{ln.quantity} {ln.uom_code || "EA"} × {ln.price} {ln.currency_code} = {lineAmount(ln).toFixed(2)} {ln.currency_code}</div>
            {(ln.schedules || []).map((sch, j) => (
              <div key={j} className="text-gray-400 pl-2">
                Schedule {sch.schedule_number}: {sch.quantity} · need-by {sch.need_by_date || "—"}
                {sch.ship_to_location_code ? ` · ship to ${sch.ship_to_location_code}` : ""}
                {sch.deliver_to_location_code ? ` · deliver to ${sch.deliver_to_location_code}` : ""}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between"><span className="text-[11px] text-gray-400">Total</span><span className="text-[13px] font-semibold text-gray-900">{sum.toFixed(2)} {ccy}</span></div>
    </div>
  );
}

// Inline chat card version of the confirm/review step — rolled back from a
// full-screen popup per request; same fields/lines/buttons, embedded in the
// chat stream like every other card (FieldCard, PdfCard, ParsedDocCard).
function ConfirmCard({ draft, busy, onConfirm, onKeepEditing }) {
  return (
    <div className="border border-gray-200 rounded-xl bg-white p-3.5 max-w-[540px] shadow-sm">
      <div className="text-[13px] font-bold text-gray-900 mb-2.5">Review Purchase Order</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {poDraftToFields(draft).map((f, i) => (
          <div key={i} className="bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">{f.label}</p>
            <p className="text-[12px] font-medium text-gray-900 break-words">{f.value}</p>
          </div>
        ))}
      </div>
      <PoLinesBreakdown draft={draft} />
      <div className="flex gap-2 mt-3">
        <button onClick={onConfirm} disabled={busy}
          className="flex-1 py-2 rounded-lg font-bold text-white text-[12px] disabled:opacity-50"
          style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}>
          ✓ Submit to Oracle
        </button>
        <button onClick={onKeepEditing} disabled={busy}
          className="flex-1 py-2 rounded-lg font-semibold text-[12px] border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50">
          ✕ Keep Editing
        </button>
      </div>
    </div>
  );
}

/* ── composer ────────────────────────────────────────────────────────────── */
// Quick-start prompts shown when the box is empty, plus this session's own
// recent messages when the user has typed something — real, working
// suggestions rather than a static list only.
const QUICK_PROMPTS = [
  "Create a new purchase order for…",
  "Create a purchase order from requisition …",
  "Recreate purchase order …",
  "List available procurement business units",
  "Show my recent purchase orders",
];

function Composer({ value, onChange, onSend, onAttach, busy, placeholder, history }) {
  const [showSuggest, setShowSuggest] = React.useState(false);
  const suggestions = React.useMemo(() => {
    const recents = (history || []).slice(-5).reverse();
    const pool = [...recents, ...QUICK_PROMPTS];
    const q = value.trim().toLowerCase();
    const filtered = q ? pool.filter((s) => s.toLowerCase().includes(q)) : pool;
    // De-dupe while preserving order.
    return [...new Set(filtered)].slice(0, 5);
  }, [value, history]);

  return (
    <div className="p-2.5 bg-white border-t border-gray-200 shrink-0 relative">
      {showSuggest && suggestions.length > 0 && (
        <div className="absolute bottom-full left-2.5 right-2.5 mb-1 bg-white border border-gray-200 rounded-lg shadow-md max-h-36 overflow-y-auto z-10">
          {suggestions.map((s, i) => (
            <button key={i} type="button" onMouseDown={() => { onChange(s); setShowSuggest(false); }}
              className="w-full text-left px-3 py-1.5 text-[12px] text-gray-700 hover:bg-blue-50 border-b border-gray-50 last:border-0">
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea rows={1} value={value} onChange={(e) => onChange(e.target.value)}
          onFocus={() => setShowSuggest(true)} onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); setShowSuggest(false); onSend(); } }}
          placeholder={placeholder} className="flex-1 resize-none max-h-28 px-3 py-2.5 text-[13px] border border-gray-200 rounded-xl outline-none focus:border-[#0020C2]" />
        {onAttach && <button onClick={onAttach} disabled={busy} title="Attach a document" className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:text-[#0020C2] hover:border-[#0020C2] disabled:opacity-40"><Paperclip size={17} /></button>}
        <button onClick={onSend} disabled={busy || !value.trim()} className="p-2.5 rounded-xl text-white disabled:opacity-40" style={{ background: BRAND }}><PaperPlaneRight size={17} weight="fill" /></button>
      </div>
    </div>
  );
}

/* ── wizard panel (used in both pre-chat and chat mode) ──────────────────── */
function WizardPanel({ values, onChange, fieldErrors, fieldWarnings, result, validating, busy, onValidate, onCreate, compact, user }) {
  const ok = result && ["ok", "success"].includes((result.status || "").toLowerCase());
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {!compact && (
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Guided wizard
            <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">· direct create, no AI</span>
          </div>
        )}
        {/* Read-only Buyer line, sourced from the logged-in user — shown in BOTH the
            pre-chat (compact) and chat-mode renders of this shared panel. Buyer is
            PO's Preparer-equivalent per the plan's decision — auto-filled from the
            logged-in user's email, never typed in by hand. The free-text
            `buyer_email` field (from WizardFields, currently still required in
            erp_agents.py's field list) stays visible until the backend marks it
            hidden — harmless temporary duplication, expected until that lands. */}
        <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
          <UserIcon size={11} weight="bold" className="text-gray-400 shrink-0" />
          <span className="text-gray-400">Buyer:</span>
          <span className="font-medium text-gray-700">{user?.email || "—"} (you)</span>
        </div>
        <WizardFields agentKey={AGENT_KEY} values={values} onChange={onChange}
          missingFields={Object.keys(fieldErrors)} fieldErrors={fieldErrors} fieldWarnings={fieldWarnings} />
        {result && (
          <div className={`border px-3 py-2 text-xs flex items-start gap-2 rounded ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-[#DC2626] bg-red-50 text-[#DC2626]"}`}>
            {ok ? <CheckCircle size={13} weight="fill" className="mt-0.5 shrink-0" /> : <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />}
            <span className="font-bold">{typeof result.message === "string" ? result.message : JSON.stringify(result.message)}{result.order_number && <span className="font-mono ml-1 opacity-70">#{result.order_number}</span>}</span>
          </div>
        )}
      </div>
      <div className="shrink-0 border-t border-gray-200 p-3 bg-gray-50 flex gap-2">
        <button onClick={onValidate} disabled={validating || busy}
          className="flex-1 border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 rounded flex items-center justify-center gap-1.5">
          <Sparkle size={12} weight="fill" />{validating ? "Validating…" : "Dry-run validate"}
        </button>
        <button onClick={onCreate} disabled={busy || validating}
          className="flex-1 bg-[#0020C2] text-white px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 rounded flex items-center justify-center gap-1.5">
          {busy ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <PaperPlaneRight size={12} weight="fill" />}
          {busy ? "Creating…" : "Send to Oracle Fusion"}
        </button>
      </div>
    </div>
  );
}

/* ── PO dashboard (live from Oracle) ─────────────────────────────────────── */
// NOTE on Drop/defer (per plan): PR's batch/Excel multi-line flow (BatchCard/
// BatchResultCard), the two chat-mode existing-record edit confirms
// (AddLineConfirmCard/UpdateConfirmCard), PR's bulk-submit-for-approval cards, and
// PostCreateCard's charge-account/funds-check post-create step are intentionally
// NOT ported in this first cut — PO backend semantics for those (schedule/
// distribution child paths, whether a funds-check-at-draft-stage equivalent even
// exists) are unverified per the plan's risk callouts, and runner.py's confirm_po/
// submit_po already confirm there is no charge-account/funds payload on PO's create
// response the way there is on PR's. Dashboard/list/record-detail/line-editor is
// kept and built PO-native from day one, per the plan.
const STATUS_TABS = [
  { key: "all",     label: "All" },
  { key: "draft",   label: "Draft",   match: (s) => /incomplete|draft|rejected by/i.test(s) },
  { key: "pending", label: "Pending", match: (s) => /pending|in process|pre-?approved|submitted/i.test(s) },
  { key: "open",    label: "Open",    match: (s) => /^open$/i.test(s) },
  { key: "closed",  label: "Closed",  match: (s) => /closed|cancell?ed|rejected|withdrawn/i.test(s) },
];
function statusTone(s) {
  if (/^open$/i.test(s)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (/reject|cancell?ed|withdraw|closed/i.test(s)) return "bg-red-50 text-red-700 border-red-200";
  if (/pending|process|submit/i.test(s)) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-gray-50 text-gray-600 border-gray-200";
}
function DashStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex-1 border border-gray-200 rounded-xl p-3 bg-white">
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mb-1"><Icon size={13} className={accent} />{label}</div>
      <div className="text-2xl font-black text-gray-900">{value}</div>
    </div>
  );
}

// Action set per the plan (po_agent.py mirrors pr_agent.py's route surface,
// adapted to PO's verified Oracle action list — no "withdraw"/"return-lines"
// equivalent, those are PR-only concepts): acknowledge, cancel, close,
// communicate, duplicate, freeze, unfreeze, hold, removeHold, renumberPO,
// reopen. Route paths/body shapes below are a best-effort mirror of PR's
// `${BASE}/<verb>` + `{ po_id, reason? }` convention — po_agent.py does not exist
// yet at the time of writing, so these are assumptions, not verified contracts
// (documented in the task report).
const ACTION_ROUTE = {
  acknowledge: "acknowledge", cancel: "cancel", close: "close", communicate: "communicate",
  duplicate: "duplicate", freeze: "freeze", unfreeze: "unfreeze", hold: "hold",
  remove_hold: "remove-hold", renumber: "renumber", reopen: "reopen",
};
const ACTION_LABEL = {
  acknowledge: "Acknowledge this purchase order?", cancel: "Cancel this purchase order?",
  close: "Close this purchase order?", hold: "Place this purchase order on hold?",
  remove_hold: "Remove the hold on this purchase order?", freeze: "Freeze this purchase order?",
  unfreeze: "Unfreeze this purchase order?", reopen: "Reopen this purchase order?",
  renumber: "Renumber this purchase order?",
};

function RecordDetail({ rec, headerId, onClose, onChanged }) {
  const h = rec.record || {};
  const lines = rec.lines || [];
  const id = headerId || h.POHeaderId;
  const status = h.Status || "";
  const isDraft = /incomplete|draft/i.test(status);

  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);            // {tone,text}
  const [attachments, setAttachments] = React.useState(null);
  const [confirm, setConfirm] = React.useState(null);     // {type, reason, newNumber}

  // NOTE: unlike PR's RecordDetail, this does NOT have Approvers/Schedules/
  // Distributions viewers or an Edit/Add-line editor — as of this cut, the real
  // app/api/po_agent.py has no /approvers, /schedules, /distributions, /update, or
  // /lines routes (its own module docstring flags them "not present... flagged for
  // a follow-up once runner.py grows them"). Wiring UI to routes proven not to
  // exist would just 404 on click, so those are left out rather than stubbed.
  const run = async (promise, okText) => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await promise;
      const ok = data.status === "ok";
      setMsg({ tone: ok ? "success" : "error", text: data.message || (ok ? okText : "Action failed.") });
      if (ok) onChanged?.();
    } catch (e) { setMsg({ tone: "error", text: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); setConfirm(null); }
  };
  const [showPdf, setShowPdf] = React.useState(false);   // embed the PDF in-place, not a new tab
  const loadAttachments = async () => {
    setBusy(true);
    try { const { data } = await api.get(`${BASE}/attachments/${id}`); setAttachments(data.attachments || []); }
    catch { setAttachments([]); } finally { setBusy(false); }
  };

  // po_agent.py's lifecycle routes read the target PO via payload.order /
  // order_number / po_header_id (see _order_from) — po_header_id is the one this
  // view always has on hand.
  const runAction = (type, extra) => {
    const route = ACTION_ROUTE[type];
    const body = { po_header_id: id, ...(extra || {}) };
    run(api.post(`${BASE}/${route}`, body), `${type.replace(/_/g, " ")} applied.`);
  };

  const ActionBtn = ({ icon: Icon, label, onClick, danger }) => (
    <button disabled={busy} onClick={onClick}
      className={`flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border disabled:opacity-50 ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="absolute inset-0 bg-white z-20 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
        <button onClick={onClose} className="flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-gray-800"><ArrowLeft size={15} /> Back to list</button>
        {rec.po_url && <button onClick={() => window.open(rec.po_url, "_blank")} className="flex items-center gap-1 text-[11px] text-[#0020C2] hover:underline">Open in Oracle (REST) <ArrowSquareOut size={12} /></button>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div>
          <div className="text-xl font-black text-gray-900">{h.OrderNumber || h.POHeaderId}</div>
          <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border ${statusTone(h.Status || "")}`}>{h.Status || "—"}</span>
        </div>

        {/* actions bar — PO's own verified lifecycle action set (feature_fix02.md
            §13.2), not a 1:1 port of PR's withdraw/cancel/delete */}
        <div className="flex flex-wrap gap-2">
          <ActionBtn icon={FilePdf} label={showPdf ? "Hide PDF" : "View PDF"} onClick={() => setShowPdf((v) => !v)} />
          <ActionBtn icon={Paperclip} label="Attachments" onClick={loadAttachments} />
          {!isDraft && <ActionBtn icon={CheckCircle} label="Acknowledge" onClick={() => runAction("acknowledge")} />}
          {!isDraft && <ActionBtn icon={Envelope} label="Communicate" onClick={() => runAction("communicate")} />}
          <ActionBtn icon={Copy} label="Duplicate" onClick={() => runAction("duplicate")} />
          {!isDraft && <ActionBtn icon={Hash} label="Renumber" onClick={() => setConfirm({ type: "renumber", newNumber: "" })} />}
          {!isDraft && <ActionBtn icon={LockSimple} label="Freeze" onClick={() => setConfirm({ type: "freeze" })} />}
          {!isDraft && <ActionBtn icon={LockSimpleOpen} label="Unfreeze" onClick={() => setConfirm({ type: "unfreeze" })} />}
          {!isDraft && <ActionBtn icon={HandPalm} label="Hold" onClick={() => setConfirm({ type: "hold", reason: "" })} />}
          {!isDraft && <ActionBtn icon={HandPalm} label="Remove hold" onClick={() => setConfirm({ type: "remove_hold" })} />}
          {/^closed/i.test(status) && <ActionBtn icon={ArrowCounterClockwise} label="Reopen" onClick={() => setConfirm({ type: "reopen" })} />}
          {/^open$/i.test(status) && <ActionBtn icon={XCircle} label="Close" danger onClick={() => setConfirm({ type: "close" })} />}
          {(isDraft || !/closed|cancell?ed/i.test(status)) && <ActionBtn icon={Prohibit} label="Cancel" danger onClick={() => setConfirm({ type: "cancel", reason: "" })} />}
        </div>

        {msg && (
          <div className={`text-[12px] px-3 py-2 rounded-lg border flex items-start gap-2 ${msg.tone === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"}`}>
            {msg.tone === "success" ? <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0" /> : <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />}
            <span>{msg.text}</span>
          </div>
        )}

        {showPdf && <PdfViewer path={`${BASE}/pdf/${id}`} filename={`purchase_order_${h.OrderNumber || id}.pdf`} height={480} />}

        {/* confirmation prompt for destructive/state-changing actions */}
        {confirm && (
          <div className="border-2 rounded-xl p-3 bg-white shadow-sm" style={{ borderColor: ["cancel", "close", "hold"].includes(confirm.type) ? "#DC2626" : BRAND }}>
            <div className="text-[12px] font-semibold text-gray-900 mb-1.5">{ACTION_LABEL[confirm.type] || "Apply this action?"}</div>
            {(confirm.type === "cancel" || confirm.type === "hold") && (
              <textarea value={confirm.reason} onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })}
                rows={2} placeholder="Reason (optional)" className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 outline-none focus:border-[#0020C2]" />
            )}
            {confirm.type === "renumber" && (
              <input value={confirm.newNumber} onChange={(e) => setConfirm({ ...confirm, newNumber: e.target.value })}
                placeholder="New order number" className="w-full text-[12px] border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 outline-none focus:border-[#0020C2]" />
            )}
            <div className="flex gap-2">
              <button disabled={busy || (confirm.type === "renumber" && !confirm.newNumber.trim())}
                onClick={() => runAction(confirm.type,
                  confirm.type === "renumber" ? { new_number: confirm.newNumber.trim() }
                  : confirm.type === "cancel" || confirm.type === "hold" ? { reason: confirm.reason || null }
                  : undefined)}
                className="text-[12px] font-medium text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                style={{ background: ["cancel", "close", "hold"].includes(confirm.type) ? "#DC2626" : BRAND }}>
                {busy ? "Working…" : "Confirm"}
              </button>
              <button disabled={busy} onClick={() => setConfirm(null)} className="text-[12px] text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200">Keep it</button>
            </div>
          </div>
        )}

        {/* attachments */}
        {attachments && (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">Attachments</div>
            {attachments.length === 0 ? <div className="px-3 py-2 text-[12px] text-gray-400">No attachments on this purchase order.</div>
              : attachments.map((a, i) => (
                <div key={i} className="px-3 py-2 text-[12px] border-b border-gray-50 last:border-0 flex items-center gap-2">
                  <FilePdf size={14} className="text-gray-400 shrink-0" />
                  <div className="min-w-0 flex-1"><div className="text-gray-900 truncate">{a.FileName || a.Title || "Attachment"}</div>{a.Description && <div className="text-[10px] text-gray-400 truncate">{a.Description}</div>}</div>
                  <button disabled={busy} onClick={() => downloadViaApi(`${BASE}/attachment/${id}/${a.AttachedDocumentId}`, a.FileName || "attachment")}
                    className="text-[11px] text-[#0020C2] hover:underline flex items-center gap-1 shrink-0"><DownloadSimple size={13} /> Download</button>
                </div>
              ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
          <div><div className="text-gray-400">Description</div><div className="text-gray-900">{h.Description || "—"}</div></div>
          <div><div className="text-gray-400">Procurement BU</div><div className="text-gray-900">{h.ProcurementBU || "—"}</div></div>
          <div><div className="text-gray-400">Supplier</div><div className="text-gray-900">{h.Supplier || "—"}{h.SupplierSite ? ` · ${h.SupplierSite}` : ""}</div></div>
          <div><div className="text-gray-400">Buyer</div><div className="text-gray-900">{h.Buyer || "—"}</div></div>
          <div><div className="text-gray-400">Created</div><div className="text-gray-900">{(h.CreationDate || "").slice(0, 10) || "—"}</div></div>
          <div><div className="text-gray-400">Currency</div><div className="text-gray-900">{h.CurrencyCode || "—"}</div></div>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Lines ({lines.length})</div>
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            {lines.length === 0 && <div className="px-3 py-2 text-[12px] text-gray-400">No line detail returned.</div>}
            {lines.map((ln, i) => (
              <div key={i} className="px-3 py-2 text-[12px] border-b border-gray-50 last:border-0">
                <div className="font-medium text-gray-900">{ln.LineNumber}. {ln.ItemDescription || ln.Item || "Line"}</div>
                <div className="text-gray-500">
                  {ln.Quantity != null ? `${ln.Quantity} ${ln.UOMCode || ""} × ${ln.Price ?? "—"} ${ln.CurrencyCode || ""}` : (ln.LineTypeCode || "")}
                </div>
                {(ln.schedules || []).map((sch, j) => (
                  <div key={j} className="text-gray-400">
                    Sched {sch.ScheduleNumber || j + 1}{sch.ShipToLocationCode ? ` · to ${sch.ShipToLocationCode}` : ""}{sch.NeedByDate ? ` · by ${String(sch.NeedByDate).slice(0, 10)}` : ""}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PoDashboard() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [tab, setTab] = React.useState("all");
  const [detail, setDetail] = React.useState(null);
  const [detailId, setDetailId] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setErr("");
    try {
      // A non-empty search routes to /search (structured filters). A term that
      // looks like an order number is matched exactly; otherwise it's a
      // description prefix search. Empty search → the full recent list.
      const q = search.trim();
      const isNumber = /-|^\d{3,}$/.test(q);
      const path = q ? `${BASE}/search` : `${BASE}/list`;
      const params = q ? (isNumber ? { number: q, limit: 200 } : { q, limit: 200 }) : { limit: 200 };
      const { data } = await api.get(path, { params });
      if (data.status === "unconfigured") { setErr("Connect your Oracle profile to view purchase orders."); setRows([]); }
      else if (data.status !== "ok") { setErr(data.message || "Couldn't load purchase orders."); setRows([]); }
      else setRows(data.items || []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); setRows([]); }
    finally { setLoading(false); }
  }, [search]);
  React.useEffect(() => { const t = setTimeout(load, search ? 320 : 0); return () => clearTimeout(t); }, [load, search]);

  const openRecord = async (id) => {
    setDetailLoading(true); setDetail({ loading: true }); setDetailId(id);
    try {
      const { data } = await api.get(`${BASE}/record/${id}`);
      if (data.status === "ok") setDetail(data); else { setDetail(null); }
    } catch { setDetail(null); }
    finally { setDetailLoading(false); }
  };
  const closeDetail = () => { setDetail(null); setDetailId(null); };
  const afterChange = () => { closeDetail(); load(); };

  const counts = React.useMemo(() => {
    const c = { total: rows.length, open: 0, pending: 0, draft: 0 };
    rows.forEach((r) => {
      const s = r.Status || "";
      if (/^open$/i.test(s)) c.open++;
      else if (/pending|process|submit/i.test(s)) c.pending++;
      else if (/incomplete|draft/i.test(s)) c.draft++;
    });
    return c;
  }, [rows]);

  const filtered = React.useMemo(() => {
    const t = STATUS_TABS.find((x) => x.key === tab);
    if (!t || !t.match) return rows;
    return rows.filter((r) => t.match(r.Status || ""));
  }, [rows, tab]);

  return (
    <div className="relative flex-1 min-h-0 flex flex-col bg-gray-50">
      {detail && !detail.loading && <RecordDetail rec={detail} headerId={detailId} onClose={closeDetail} onChanged={afterChange} />}
      {detailLoading && <div className="absolute inset-0 bg-white/70 z-30 flex items-center justify-center text-[12px] text-gray-500">Loading record…</div>}

      <div className="p-4 space-y-3 overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5"><SquaresFour size={12} weight="fill" /> Purchase Orders</div>
          <button onClick={load} disabled={loading} className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#0020C2] disabled:opacity-50"><ArrowClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh</button>
        </div>

        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
          <MagnifyingGlass size={14} className="text-gray-400 shrink-0" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order number or description…"
            className="w-full text-[12px] outline-none" />
          {search && <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-700 shrink-0"><X size={14} /></button>}
        </div>

        <div className="flex gap-2">
          <DashStat icon={ClipboardText} label="Total" value={counts.total} accent="text-gray-400" />
          <DashStat icon={CheckCircle} label="Open" value={counts.open} accent="text-emerald-500" />
          <DashStat icon={Clock} label="Pending" value={counts.pending} accent="text-amber-500" />
          <DashStat icon={Receipt} label="Draft" value={counts.draft} accent="text-[#0020C2]" />
        </div>

        <div className="flex gap-4 border-b border-gray-200">
          {STATUS_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`pb-2 text-[12px] font-medium border-b-2 -mb-px ${tab === t.key ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {err && <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{err}</div>}
        {loading ? <div className="text-[12px] text-gray-400 py-6 text-center">Loading purchase orders from Oracle…</div>
          : filtered.length === 0 && !err ? <div className="text-[12px] text-gray-400 py-6 text-center">No purchase orders in this view.</div>
          : (
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              {filtered.map((r) => (
                <button key={r.POHeaderId} onClick={() => openRecord(r.POHeaderId)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-gray-50 last:border-0 hover:bg-blue-50/50">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-gray-900 truncate">{r.OrderNumber || r.POHeaderId}</div>
                    <div className="text-[11px] text-gray-500 truncate">{r.Description || "—"} · {r.ProcurementBU || ""}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${statusTone(r.Status || "")}`}>{r.Status || "—"}</span>
                  <span className="text-[10px] text-gray-400 shrink-0 w-20 text-right">{(r.CreationDate || "").slice(0, 10)}</span>
                  <CaretRight size={13} className="text-gray-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

/* ── main modal (centered, matches the other agents) ─────────────────────── */
export default function PurchaseOrderAgentModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile } = useErp();
  const { user } = useAuth();
  const [sessionId] = React.useState(() => crypto.randomUUID());
  const [chatStarted, setChatStarted] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [phraseIdx, setPhraseIdx] = React.useState(0);
  const [view, setView] = React.useState("create");   // "create" | "dashboard"
  // Most recently picked procurement_bu/supplier ids, so a dependent field card
  // (supplier_site ← supplier) can scope its dropdown.
  const [lookupContext, setLookupContext] = React.useState({});
  const onRawPick = async (field, row, itemId) => {
    if (field === "procurement_bu") setLookupContext((c) => ({ ...c, buId: row.value }));
    if (field === "supplier") setLookupContext((c) => ({ ...c, supplierId: row.value }));
    if (field === "buyer") {
      // Buyer never flows through the LLM — set it directly, bypassing send().
      setItems((xs) => xs.map((x) => (x.id === itemId ? { ...x, answered: true } : x)));
      const loadingId = pushLoading(); setBusy(true);
      try {
        await api.post(`${BASE}/set-buyer`, { session_id: sessionId, buyer_id: row.value });
        resolveLoading(loadingId, `Buyer set to ${row.label}. Please repeat your request.`, "success");
      } catch (e) {
        resolveLoading(loadingId, `Couldn't set the buyer: ${e?.response?.data?.detail || e.message}`, "error");
      } finally { setBusy(false); }
    }
  };

  // guided wizard (manual path)
  const [wizardValues, setWizardValues] = React.useState({});
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldWarnings, setFieldWarnings] = React.useState({});
  const [wizardResult, setWizardResult] = React.useState(null);
  const [validating, setValidating] = React.useState(false);
  const [wizardBusy, setWizardBusy] = React.useState(false);

  const scrollRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const isOpen = openAgent === AGENT_KEY;

  React.useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [items]);
  React.useEffect(() => { if (!busy) return; const t = setInterval(() => setPhraseIdx((i) => i + 1), 1800); return () => clearInterval(t); }, [busy]);

  // Feeds the composer's quick-suggestions with this session's own recent
  // messages, alongside the static starter prompts. Must be declared before the
  // early `isOpen` return below (hooks can't be conditional).
  const userHistory = React.useMemo(
    () => items.filter((x) => x.kind === "chat" && x.role === "user").map((x) => x.text),
    [items]
  );

  if (!isOpen) return null;
  const close = () => setOpenAgent(null);

  const push = (item) => setItems((xs) => [...xs, { id: Date.now() + Math.random(), ...item }]);
  const pushLoading = () => { const id = Date.now() + Math.random(); setItems((xs) => [...xs, { id, kind: "chat", role: "assistant", loading: true }]); return id; };
  const resolveLoading = (id, text, tone) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, loading: false, text, tone } : x)));

  const payloadFV = () => {
    const out = {};
    for (const [k, v] of Object.entries(wizardValues)) out[k] = Array.isArray(v) && v[0]?.attachment_id ? v.map((i) => i.attachment_id) : v;
    return out;
  };

  async function send(text) {
    const userText = (text ?? input).trim();
    if (!userText || busy) return;
    setInput(""); setChatStarted(true);
    // Dismiss any prior interactive cards so a follow-up never re-shows a stale
    // confirmation/field card (e.g. after a rejected create). A fresh one is
    // pushed only if the agent re-stages this turn.
    setItems((xs) => xs.filter((x) => x.kind !== "confirm").map((x) => (x.kind === "field" ? { ...x, answered: true } : x)));
    push({ kind: "chat", role: "user", text: userText });
    const loadingId = pushLoading(); setBusy(true);
    try {
      const { data } = await api.post(`${BASE}/chat`, { session_id: sessionId, message: userText });
      if (data.status === "unconfigured") { resolveLoading(loadingId, data.response); setSetupOpen?.(true); return; }
      resolveLoading(loadingId, data.response || "…");
      setDraft(data.draft || null);
      if (data.awaiting_confirmation && data.draft) {
        // Inline confirm card — see the ChatStream render switch below.
        push({ kind: "confirm", draft: data.draft });
      }
      else if (data.next_field_ui) push({ kind: "field", fieldUi: data.next_field_ui, answered: false });
      // A PDF the agent made available this turn (independent of the above).
      if (data.pdf_ready) push({ kind: "pdf", info: data.pdf_ready });
    } catch (e) { resolveLoading(loadingId, `Something went wrong: ${e?.response?.data?.detail || e.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function confirmSubmit() {
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/confirm`, { session_id: sessionId });
      const ok = data.status === "ok";
      resolveLoading(loadingId, data.message || (ok ? "Created." : "Oracle rejected the purchase order."), ok ? "success" : "error");
      if (ok) {
        // A purchase order's create response carries no charge-account/funds
        // payload the way a PR's does (confirm_po only returns order_number/
        // po_header_id/order_status/order_url), so there is no PostCreateCard-
        // equivalent two-step to show right after; the success bubble above
        // already carries the order number.
        setDraft(null);
        setItems((xs) => xs.filter((x) => x.kind !== "confirm"));
        // Immediately submit the freshly-created draft for approval
        // (draftPurchaseOrders/action/submit — the INITIAL submit for a draft
        // just created in this session, not the purchaseOrders resubmit path).
        const subId = pushLoading();
        try {
          const sub = await api.post(`${BASE}/submit`, { session_id: sessionId });
          resolveLoading(subId, sub.data?.message || "Submitted for approval.", sub.data?.status === "ok" ? "success" : "error");
        } catch (e) {
          resolveLoading(subId, `Created, but submitting for approval failed: ${e?.response?.data?.detail || e.message}`, "error");
        }
      }
      // On rejection the confirm card stays so the user can fix values and resubmit.
    } catch (e) {
      resolveLoading(loadingId, `Submission failed: ${e?.response?.data?.detail || e.message}`, "error");
    } finally { setBusy(false); }
  }
  async function keepEditing() {
    setBusy(true); const loadingId = pushLoading();
    try {
      const { data } = await api.post(`${BASE}/reject`, { session_id: sessionId });
      resolveLoading(loadingId, data.message || "Okay — tell me what to change.");
      setDraft(null);
      setItems((xs) => xs.filter((x) => x.kind !== "confirm"));
    }
    catch { resolveLoading(loadingId, "Okay — tell me what to change."); } finally { setBusy(false); }
  }
  // Quick-action alternate entry point into the SAME inline confirm card used by
  // normal chat: read the already-picked Procurement BU from the wizard, ask the
  // backend to analyze similar POs in it, and — on success — push the returned
  // draft as a "confirm" chat item exactly like awaiting_confirmation does.
  async function createSimilar() {
    const buId = wizardValues.procurement_bu;
    if (!buId || busy) return;
    setChatStarted(true);
    setBusy(true);
    const loadingId = pushLoading();
    try {
      // po_agent.py's /similar-in-bu reads {session_id, bu_id} (session_id is
      // required — the analysis is staged, if valid, into this chat session).
      const { data } = await api.post(`${BASE}/similar-in-bu`, { session_id: sessionId, bu_id: buId });
      if (data.status === "ok" && data.draft) {
        resolveLoading(loadingId, data.message || "Based on similar POs in this Business Unit, here's a draft — review and confirm below.", "success");
        setDraft(data.draft);
        push({ kind: "confirm", draft: data.draft });
      } else {
        resolveLoading(loadingId, data.message || "Couldn't build a draft from similar purchase orders in this Business Unit.", "error");
      }
    } catch (e) {
      resolveLoading(loadingId, `Couldn't create a similar PO: ${e?.response?.data?.detail || e.message}`, "error");
    } finally {
      setBusy(false);
    }
  }

  async function clearChat() {
    try { await api.post(`${BASE}/clear`, { session_id: sessionId }); } catch { /* ignore */ }
    setItems([]); setDraft(null); setInput(""); setChatStarted(false);
  }
  async function onFile(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || busy) return;
    setChatStarted(true);
    const url = URL.createObjectURL(file);
    // Whatever the user already typed before attaching is their STATED INTENT for
    // this upload (e.g. "create a new PO from this" / "attach to PO 12345").
    const statedIntent = input.trim();
    setInput("");
    push({ kind: "chat", role: "user", text: `Attached: ${file.name}` });
    push({ kind: "file_chip", name: file.name, url });   // chip only — no content dump
    setBusy(true); const loadingId = pushLoading();
    try {
      const form = new FormData(); form.append("session_id", sessionId); form.append("file", file);
      const { data } = await api.post(`${BASE}/upload`, form);
      if (data.parsed && data.needs_verification) {
        resolveLoading(loadingId, "I've read your document — please check the details below.");
        push({ kind: "parsed_doc", parsed: data.parsed, fileName: file.name, statedIntent });
      } else {
        resolveLoading(loadingId, "Document received.");
        await send(statedIntent || `I've uploaded a document: ${file.name}.`);
      }
    } catch (err) { resolveLoading(loadingId, `Could not process the file: ${err?.response?.data?.detail || err.message}`, "error"); }
    finally { setBusy(false); }
  }
  async function dryRunValidate() {
    setValidating(true); setFieldErrors({}); setFieldWarnings({}); setWizardResult(null);
    try { const { data } = await api.post(`${BASE}/validate`, { field_values: payloadFV() }); setFieldErrors(data.field_errors || {}); setFieldWarnings(data.field_warnings || {}); setWizardResult({ status: data.ok ? "ok" : "error", message: data.message }); }
    catch (e) { setWizardResult({ status: "error", message: e?.response?.data?.detail || e.message }); } finally { setValidating(false); }
  }
  async function createInOracle() {
    setWizardBusy(true); setWizardResult(null); setFieldErrors({}); setFieldWarnings({});
    try {
      const { data } = await api.post(`${BASE}/manual`, payloadFV());
      setWizardResult(data);
      if (data?.field_errors) setFieldErrors(data.field_errors);
      if (data?.field_warnings) setFieldWarnings(data.field_warnings);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw) ? raw.map((x) => `${Array.isArray(x.loc) ? x.loc.join(" → ") : x.loc}: ${x.msg}`).join(" · ") : (raw && typeof raw === "object") ? JSON.stringify(raw) : (raw || e.message);
      setWizardResult({ status: "error", message: msg });
    } finally { setWizardBusy(false); }
  }

  const wizardPanelProps = {
    values: wizardValues, onChange: setWizardValues, fieldErrors, fieldWarnings,
    result: wizardResult, validating, busy: wizardBusy, onValidate: dryRunValidate, onCreate: createInOracle,
    user,
  };

  const ChatStream = (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-gray-50">
      {items.map((it) => {
        if (it.kind === "chat") return <Bubble key={it.id} msg={it} phraseIdx={phraseIdx} />;
        if (it.kind === "confirm") return <div key={it.id} className="pl-8"><ConfirmCard draft={it.draft} busy={busy} onConfirm={confirmSubmit} onKeepEditing={keepEditing} /></div>;
        if (it.kind === "field") return <div key={it.id} className="pl-8"><FieldCard fieldUi={it.fieldUi} disabled={it.answered} onSubmit={(v) => send(v)}
          onSkip={() => setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, answered: true } : x)))}
          onRawPick={(field, row) => onRawPick(field, row, it.id)} lookupContext={lookupContext} /></div>;
        if (it.kind === "pdf") return <div key={it.id} className="pl-8"><PdfCard info={it.info} busy={busy} /></div>;
        if (it.kind === "file_chip") return <div key={it.id} className="flex justify-end"><FileChip name={it.name} url={it.url} /></div>;
        if (it.kind === "parsed_doc") return <div key={it.id} className="pl-8"><ParsedDocCard parsed={it.parsed} fileName={it.fileName} busy={busy}
          onCreateNew={() => { setItems((xs) => xs.filter((x) => x.id !== it.id)); send(it.statedIntent || "Create a new purchase order from the document I uploaded."); }}
          onAttachExisting={() => { setItems((xs) => xs.filter((x) => x.id !== it.id)); send("Attach the uploaded document to an existing purchase order."); }} /></div>;
        return null;
      })}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4" onMouseDown={close}>
      <style>{`
        .po-modal-pre  { width:720px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .po-modal-chat { width:min(94vw,1200px); height:90vh; }
      `}</style>
      <div onMouseDown={(e) => e.stopPropagation()}
        className={`bg-white text-gray-900 rounded-2xl border border-gray-300 shadow-2xl flex flex-col overflow-hidden ${(chatStarted || view === "dashboard") ? "po-modal-chat" : "po-modal-pre"}`}
        style={{ transition: "width .4s cubic-bezier(0.4,0,0.2,1), height .4s cubic-bezier(0.4,0,0.2,1)" }}>

        {/* header */}
        <div className="px-5 py-3.5 border-b border-gray-200 flex items-start justify-between shrink-0 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion ERP · Vaku AI</div>
            <h3 className="text-xl font-black tracking-tight mt-0.5">Purchase Order Create</h3>
            <p className="text-xs text-gray-500 mt-0.5">Guided wizard or AI chat — validated against Oracle before submit</p>
          </div>
          {/* Create / Dashboard toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 shrink-0 self-center">
            <button onClick={() => setView("create")}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-md flex items-center gap-1.5 ${view === "create" ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <ChatCircleText size={13} weight="bold" /> Create
            </button>
            <button onClick={() => setView("dashboard")}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-md flex items-center gap-1.5 ${view === "dashboard" ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <SquaresFour size={13} weight="bold" /> Dashboard
            </button>
          </div>
          <div className="flex items-center gap-3 shrink-0 self-center">
            <button onClick={() => { setOpenAgent(null); setSetupOpen?.(true); }} className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"><Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}</button>
            {view === "create" && <button onClick={clearChat} title="Clear" className="text-gray-400 hover:text-gray-700"><Trash size={16} /></button>}
            <button onClick={close} title="Close" className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
          </div>
        </div>

        {/* body */}
        {view === "dashboard" ? (
          <PoDashboard />
        ) : !chatStarted ? (
          /* PRE-CHAT — WizardFields centered, with a chat starter below */
          <>
            <div className="flex-1 overflow-y-auto min-h-0">
              <WizardPanel {...wizardPanelProps} compact />
            </div>
            {/* Prominent quick-action — distinct from the low-key QUICK_PROMPTS
                autocomplete dropdown in the Composer below. */}
            <div className="border-t border-gray-200 px-4 py-2.5 bg-white shrink-0">
              <button
                onClick={createSimilar}
                disabled={busy || !wizardValues.procurement_bu}
                title={!wizardValues.procurement_bu ? "Pick a Procurement BU first" : undefined}
                className="w-full bg-[#0020C2] text-white px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 rounded flex items-center justify-center gap-1.5">
                <Sparkle size={12} weight="fill" />
                Create similar to existing POs in this Business Unit
              </button>
            </div>
            <div className="border-t border-gray-200 px-4 py-1.5 bg-gray-50 shrink-0 flex items-center gap-2 text-[11px] text-gray-400">
              <Robot size={12} weight="bold" /><span>Or describe it / paste JSON below to use the AI agent instead</span>
            </div>
            <Composer value={input} onChange={setInput} onSend={() => send()} onAttach={() => fileRef.current?.click()} busy={busy}
              placeholder="Describe your purchase order or paste a JSON payload…" history={userHistory} />
          </>
        ) : (
          /* CHAT MODE — chat 60% left / wizard 40% right */
          <div className="flex flex-1 overflow-hidden min-h-0 flex-col md:flex-row">
            <div className="flex flex-col overflow-hidden border-b md:border-b-0 md:border-r border-gray-200 md:w-[60%] flex-1 min-h-0">
              {ChatStream}
              {draft && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-1.5 text-[11px] text-gray-600 shrink-0">
                  <span className="font-medium text-gray-700">Draft:</span> {draft.lines?.length || 0} line(s) · {draftTotal(draft).sum.toFixed(2)} {draftTotal(draft).ccy} · <span className="text-gray-400">BU</span> {draft.procurement_bu_name || draft.procurement_bu_id || "—"}
                </div>
              )}
              <Composer value={input} onChange={setInput} onSend={() => send()} onAttach={() => fileRef.current?.click()} busy={busy} placeholder="Continue the conversation…" history={userHistory} />
            </div>
            <div className="md:w-[40%] md:min-w-[360px] bg-white flex flex-col min-h-0">
              <WizardPanel {...wizardPanelProps} />
            </div>
          </div>
        )}

        <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={onFile} />
      </div>
    </div>
  );
}
